/**
 * Named workspaces: a study plus how you had it arranged.
 *
 * A case already persists — it is `case.json` next to its outputs. What does
 * not persist is everything around it: which volume you were looking at, what
 * you had overlaid, the layout. Reopening a case put you back at the file tree
 * with nothing loaded, so coming back to a study meant rebuilding the view by
 * hand every time.
 *
 * A workspace is that arrangement, named. It is deliberately a *pointer* to a
 * case rather than a copy of one: nothing here duplicates imaging data, and a
 * workspace whose case has been deleted says so rather than half-loading.
 *
 * Stored per machine alongside the rest of the shell state — this is a
 * convenience about how you like to work, not part of the record of a run.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface Workspace {
  id: string;
  name: string;
  /** Path to the case.json this workspace opens. */
  caseJsonPath: string;
  subjectId: string;
  /** What was on screen. All optional: an older workspace may predate them. */
  view?: {
    tab?: string;
    baseNodeId?: string | null;
    overlayNodeIds?: string[];
    layout?: string;
  };
  createdIso: string;
  lastOpenedIso: string;
}

interface WorkspaceState {
  workspaces: Workspace[];
  save: (w: Omit<Workspace, "id" | "createdIso" | "lastOpenedIso">) => Workspace;
  touch: (id: string) => void;
  remove: (id: string) => void;
  rename: (id: string, name: string) => void;
}

export const useWorkspaces = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],

      save: (draft) => {
        const now = new Date().toISOString();
        const existing = get().workspaces.find(
          (w) => w.caseJsonPath === draft.caseJsonPath && w.name === draft.name,
        );
        const workspace: Workspace = existing
          ? { ...existing, ...draft, lastOpenedIso: now }
          : {
              ...draft,
              id: `ws-${now}-${Math.random().toString(36).slice(2, 8)}`,
              createdIso: now,
              lastOpenedIso: now,
            };
        set((s) => ({
          workspaces: [workspace, ...s.workspaces.filter((w) => w.id !== workspace.id)],
        }));
        return workspace;
      },

      touch: (id) =>
        set((s) => ({
          workspaces: s.workspaces
            .map((w) => (w.id === id ? { ...w, lastOpenedIso: new Date().toISOString() } : w))
            .sort((a, b) => b.lastOpenedIso.localeCompare(a.lastOpenedIso)),
        })),

      remove: (id) => set((s) => ({ workspaces: s.workspaces.filter((w) => w.id !== id) })),

      rename: (id, name) =>
        set((s) => ({
          workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, name } : w)),
        })),
    }),
    { name: "openads.workspaces.v1" },
  ),
);

// ---------------------------------------------------------------- actions ---

import { registerAction } from "@/shell/actions";
import { appActions, useAppStore } from "@/store/appStore";
import { useViewerStore } from "@/viewer/viewerStore";
import { pickAndOpenSubject } from "@/pipeline/actions";

/**
 * Registered from `shell/bootstrap`. Saving captures what is on screen now;
 * there is nothing to save without an open study, and saying so is better than
 * writing an empty workspace that disappoints later.
 */
export function registerWorkspaceActions(): () => void {
  const offs = [
    registerAction("workspace.save", () => {
      const manifest = useAppStore.getState().currentCase;
      if (!manifest) {
        appActions.notify({
          title: "Open a study first",
          detail: "A workspace remembers a study and how you had it arranged.",
          tone: "info",
        });
        return;
      }
      const viewer = useViewerStore.getState();
      const suggested = manifest.studyName || manifest.subjectId;
      const name = window.prompt("Name this workspace", suggested);
      if (!name) return;
      useWorkspaces.getState().save({
        name,
        caseJsonPath: `${manifest.outputRoot}/${manifest.subjectId}/case.json`,
        subjectId: manifest.subjectId,
        view: {
          tab: useAppStore.getState().activeTab,
          baseNodeId: viewer.baseNodeId,
          overlayNodeIds: viewer.overlayNodeIds,
          layout: viewer.layout,
        },
      });
      appActions.notify({ title: `Saved “${name}”`, tone: "info" });
    }),

    registerAction("workspace.open", () => appActions.setActiveTab("home")),
    registerAction("workspace.new", () => void pickAndOpenSubject()),
  ];
  return () => offs.forEach((off) => off());
}
